/**
 * useCampaignAutomationParticipants - participant-view data for a campaign.
 *
 * Powers the overview's people table when a flow exists: it loads the flow (for
 * the "has automation" flag and the current-step titles) and the participants,
 * and exposes the add/remove/pause handlers wired to the automation endpoints.
 * It deliberately does NOT edit or save the block sequence: that lives only in
 * the AutomationSurface, so the two never fight over a save.
 *
 * @module components/admin/automation/useCampaignAutomationParticipants
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSupabase } from '../../../lib/SupabaseProvider';
import { requireAccessToken } from '../../../lib/auth/requireAccessToken';
import { captureException } from '../../../lib/telemetry/posthog/frontend';
import type { AutomationBlock } from '../../../lib/api-contracts/automation-blocks';
import {
  getAutomationFlow,
  listParticipants,
  addParticipants,
  deleteParticipant,
  setParticipantPaused,
  type CampaignParticipantRow,
  type AddParticipantsHandler,
  type ParticipantImportCommand,
} from '../../../services/automationService';

/** The participant-view state and handlers for a campaign. */
export interface CampaignAutomationParticipants {
  /** True until the first load resolves. */
  loading: boolean;
  /** Whether an automation flow exists for this campaign. */
  hasFlow: boolean;
  /** Whether the enabled flow has at least one step and can accept invites. */
  canInvite: boolean;
  /** The flow's blocks, for titling a participant's current step. */
  blocks: AutomationBlock[];
  /** The campaign's participants. */
  participants: CampaignParticipantRow[];
  /** Visible load or mutation failure for the overview. */
  error: string | null;
  /** Re-read the flow and participants (after enabling, for example). */
  reload: () => void;
  /** Batch-add participants and refresh the list. */
  addParticipants: AddParticipantsHandler;
  /** Remove a participant by id. */
  deleteParticipant: (id: string) => Promise<void>;
  /** Pause or resume a participant's automation. */
  togglePause: (id: string, paused: boolean) => Promise<void>;
}

/**
 * Load and mutate a campaign's automation participants.
 *
 * @param sessionId The campaign id.
 */
export function useCampaignAutomationParticipants(
  sessionId: string,
): CampaignAutomationParticipants {
  const { supabase, loading: authLoading } = useSupabase();

  const [loading, setLoading] = useState(true);
  const [hasFlow, setHasFlow] = useState(false);
  const [canInvite, setCanInvite] = useState(false);
  const [blocks, setBlocks] = useState<AutomationBlock[]>([]);
  const [participants, setParticipants] = useState<CampaignParticipantRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const mutationGenerationRef = useRef(0);
  const sessionIdRef = useRef(sessionId);
  const canInviteRef = useRef(false);
  const flowVersionRef = useRef<number | null>(null);
  sessionIdRef.current = sessionId;
  canInviteRef.current = canInvite;

  useEffect(() => {
    mutationGenerationRef.current += 1;
    flowVersionRef.current = null;
    return () => {
      mutationGenerationRef.current += 1;
    };
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId || authLoading) {
      if (authLoading) return undefined;
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setCanInvite(false);
    canInviteRef.current = false;
    void (async () => {
      try {
        const [flow, people] = await Promise.all([
          getAutomationFlow(sessionId),
          listParticipants(sessionId),
        ]);
        if (cancelled) return;
        setHasFlow(Boolean(flow));
        setBlocks(flow?.blocks ?? []);
        flowVersionRef.current = flow?.version ?? null;
        const flowCanInvite = Boolean(flow?.enabled && flow.blocks.length > 0);
        setCanInvite(flowCanInvite);
        canInviteRef.current = flowCanInvite;
        setParticipants(people);
      } catch (error) {
        captureException(error as Error, 'useCampaignAutomationParticipants.load', { sessionId });
        if (!cancelled) setError('Could not load people');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authLoading, sessionId, nonce]);

  const reload = useCallback(() => {
    setError(null);
    setNonce((n) => n + 1);
  }, []);

  const handleAdd = useCallback(
    async (command: ParticipantImportCommand) => {
      const isReceiptLifecycleRead = command.action === 'status' || command.action === 'cancel';
      if (!isReceiptLifecycleRead && !canInviteRef.current) {
        throw new Error('Automation is not ready');
      }
      const generation = mutationGenerationRef.current;
      const accessToken = await requireAccessToken(supabase);
      const reviewedCommand = command.action === 'preview'
        ? {
            ...command,
            reviewedFlowVersion: command.reviewedFlowVersion ?? flowVersionRef.current ?? undefined,
          }
        : command;
      const result = await addParticipants(sessionId, reviewedCommand, accessToken);
      if (!('phase' in result) || result.phase !== 'confirmed') return result;
      const people = await listParticipants(sessionId);
      if (
        generation !== mutationGenerationRef.current
        || sessionId !== sessionIdRef.current
      ) return result;
      setParticipants(people);
      setError(null);
      return result;
    },
    [sessionId, supabase],
  );

  const handleDelete = useCallback(
    async (id: string) => {
      const generation = mutationGenerationRef.current;
      if (sessionId === sessionIdRef.current) setError(null);
      try {
        const accessToken = await requireAccessToken(supabase);
        await deleteParticipant(id, accessToken);
        if (
          generation !== mutationGenerationRef.current
          || sessionId !== sessionIdRef.current
        ) return;
        setParticipants((prev) => prev.filter((p) => p.id !== id));
      } catch (error) {
        captureException(error as Error, 'useCampaignAutomationParticipants.delete', { sessionId });
        if (
          generation === mutationGenerationRef.current
          && sessionId === sessionIdRef.current
        ) setError('Could not remove participant');
      }
    },
    [sessionId, supabase],
  );

  const handleTogglePause = useCallback(
    async (id: string, paused: boolean) => {
      const generation = mutationGenerationRef.current;
      if (sessionId === sessionIdRef.current) setError(null);
      try {
        const accessToken = await requireAccessToken(supabase);
        await setParticipantPaused(id, paused, accessToken);
        if (
          generation !== mutationGenerationRef.current
          || sessionId !== sessionIdRef.current
        ) return;
        setParticipants((prev) => prev.map((p) => (p.id === id ? { ...p, automation_paused: paused } : p)));
      } catch (error) {
        captureException(error as Error, 'useCampaignAutomationParticipants.togglePause', { sessionId });
        if (
          generation === mutationGenerationRef.current
          && sessionId === sessionIdRef.current
        ) setError('Could not update participant');
      }
    },
    [sessionId, supabase],
  );

  return {
    loading,
    hasFlow,
    canInvite,
    blocks,
    participants,
    error,
    reload,
    addParticipants: handleAdd,
    deleteParticipant: handleDelete,
    togglePause: handleTogglePause,
  };
}
